import { isEmpty, values } from 'lodash';
import * as types from '../mutation-types';

interface State {
  requestDispatch: string | Promise<any> | null;
  isLoading: boolean;
  perPage: number;
  perPageFixed?: number;
  ordering: null | string;
  orderingDirection: '';
  currentPage: null | number;
  allPages: number;
  res: PaginatedResponse<any>;
  query: any;
  abortController: AbortController | null;
}

const emptyResponse: PaginatedResponse<any> = {
  count: 0,
  results: [],
};

function tableFactory() {
  return {
    namespaced: true,
    state: {
      requestDispatch: null,
      isLoading: true,
      perPage: 0,
      perPageFixed: undefined,
      ordering: null,
      orderingDirection: '',
      currentPage: null,
      allPages: 0,
      res: {} as PaginatedResponse<any>,
      query: {},
      abortController: null,
    } as State,
    getters: {
      showPagination: state => state.allPages > 1,
      orderingParam(state) {
        if (!state.ordering) return null;
        return state.orderingDirection + state.ordering;
      },
      isNoData: state => {
        return (
          !state.isLoading &&
          !state.res?.results?.length &&
          values(state.query).every(isEmpty)
        );
      },
      isNoDataForQuery: state => {
        return (
          !state.isLoading &&
          !state.res?.results?.length &&
          !values(state.query).every(isEmpty)
        );
      },
    },
    actions: {
      setRequestDispatch({ commit }, requestDispatch) {
        commit(types.SET_REQUEST_DISPATCH, requestDispatch);
      },
      async makeRequest({ state, getters, dispatch, commit }, page = 1) {
        if (!state.requestDispatch) return;
        const resolvedResult = await state.requestDispatch;
        if (typeof resolvedResult === 'function') {
          return resolvedResult();
        } else {
          if (state.abortController) {
            state.abortController.abort();
          }
          const newAbortController = new AbortController();
          commit(types.SET_CANCEL_SOURCE, newAbortController);

          return await dispatch(
            state.requestDispatch,
            {
              ...state.query,
              ordering: getters.orderingParam,
              page,
              signal: newAbortController?.signal,
            },
            { root: true }
          );
        }
      },
      async onPageSelect({ commit, dispatch }, page) {
        commit(types.SET_CURRENT_PAGE, page);
        const res = await dispatch('makeRequest', page);
        commit(types.SET_RES, res);
        return res;
      },
      async refresh({ commit, dispatch, state }) {
        /*
        CALL WHEN YOU ONLY WANT TO RELOAD THE CURRENT PAGE,
        AND NOT WHEN FILTERS CHANGE OR SIMILAR
        */
        const res = await dispatch('makeRequest', state.currentPage);
        commit(types.SET_RES, res);
        return res;
      },
      async reload({ state, commit, dispatch }, page: 1) {
        commit(types.SET_IS_LOADING, true);
        commit(types.SET_CURRENT_PAGE, page || 1);
        commit(types.SET_PER_PAGE, 0);
        commit(types.SET_ALL_PAGES, 0);
        commit(types.SET_RES, { ...emptyResponse });
        const res = await dispatch('makeRequest', page);
        commit(types.SET_RES, res);
        commit(types.SET_PER_PAGE, state.perPageFixed || res.results.length);
        const allPages = res.results.length
          ? Math.ceil(res.count / state.perPage)
          : 0;
        commit(types.SET_ALL_PAGES, allPages);

        commit(types.SET_IS_LOADING, false);
        return res;
      },
      async orderingBy({ commit, state, dispatch }, ordering: string) {
        if (state.ordering !== ordering) {
          commit(types.SET_ORDERING, ordering);
          commit(types.SET_ORDERING_DIRECTION, '');
        } else if (state.ordering === ordering) {
          if (state.orderingDirection === '') {
            commit(types.SET_ORDERING_DIRECTION, '-');
          } else {
            commit(types.SET_ORDERING, null);
          }
        }
        await dispatch('refresh');
      },
      async setQuery({ commit, dispatch }, query: object) {
        commit(types.SET_QUERY, query);
        await dispatch('reload');
      },
    },
    mutations: {
      [types.SET_REQUEST_DISPATCH](state: State, requestDispatch) {
        state.requestDispatch = requestDispatch;
      },
      [types.SET_RES](state: State, res) {
        state.res = res;
      },
      [types.SET_IS_LOADING](state: State, isLoading) {
        state.isLoading = isLoading;
      },
      [types.SET_CURRENT_PAGE](state: State, currentPage) {
        state.currentPage = currentPage;
      },
      [types.SET_PER_PAGE](state: State, perPage) {
        state.perPage = perPage;
      },
      [types.SET_PER_PAGE_FIXED](state: State, perPageFixed) {
        state.perPageFixed = perPageFixed;
      },
      [types.SET_ALL_PAGES](state: State, allPages) {
        state.allPages = allPages;
      },
      [types.SET_ORDERING](state: State, ordering) {
        state.ordering = ordering;
      },
      [types.SET_ORDERING_DIRECTION](state: State, orderingDirection) {
        state.orderingDirection = orderingDirection;
      },
      [types.SET_QUERY](state: State, query) {
        state.query = query;
      },
      [types.SET_CANCEL_SOURCE](state: State, abortController) {
        state.abortController = abortController;
      },
    },
  };
}

export default tableFactory;
